import {
  IconButton,
  Spacer,
  SvgChevronDown,
  SvgChevronSmallDown,
  SvgChevronSmallUp,
  SvgChevronUp,
  SvgSearch,
  Text,
  TextButton,
  TextInput,
} from '@loomhq/lens';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  toggleIsTranscriptSearchOpen,
  updateCurrentSearchIndex,
  updateTranscriptSearchInputValue,
  updateTranscriptSearchPhrase,
  updateTranscriptSearchPhraseIndexArray,
} from 'actions/shareVideo';
import {
  TRANSCRIPTION_SEARCH_CLOSED,
  TRANSCRIPTION_SEARCH_NAVIGATED,
} from 'constants/events';
import { ESCAPE } from 'constants/keyCodes';
import {
  selectCurrentSearchIndex,
  selectIsTranscriptSearchOpen,
  selectTranscriptPhrases,
  selectTranscriptSearchInputValue,
  selectTranscriptSearchPhrase,
  selectTranscriptSearchPhraseIndexArray,
} from 'selectors/transcripts';
import { selectCurrentVideoId } from 'selectors/videoPlayer';
import * as analytics from 'utilities/analytics';
import { findIndicesOfSearchPhraseInPhrases } from 'utilities/transcript/findSearchWordInTranscript';
import { Transcript } from 'utilities/transcript/types';

import { SEARCH_TRANSCRIPT_TITLE } from 'components/share-video/transcript/constants';
import styles from 'components/share-video/transcript/transcript-header/styles.module.less';

const TranscriptHeaderSearch = ({
  currentUserIsOwner,
  iconSize,
}: {
  currentUserIsOwner: boolean;
  iconSize: 'small' | 'medium';
}): JSX.Element => {
  const dispatch = useDispatch();
  const inputValue: string = useSelector(selectTranscriptSearchInputValue);
  const isTranscriptSearchOpen = useSelector(selectIsTranscriptSearchOpen);
  const textInput = useRef(null);
  const searchTerm: string = useSelector(selectTranscriptSearchPhrase);
  const searchTermIndices: Array<{ index: number; counter: number }> =
    useSelector(selectTranscriptSearchPhraseIndexArray);
  const currentSearchIndex: number = useSelector(selectCurrentSearchIndex);
  const transcript = useSelector<unknown, Transcript | null>(
    selectTranscriptPhrases
  );
  const phrases = transcript?.phrases;
  const videoId = useSelector(selectCurrentVideoId);
  const storeUserSearchedPhrase = phrase => {
    dispatch(updateTranscriptSearchInputValue(phrase));

    const trimmedPhrase = phrase.trim();

    if (trimmedPhrase !== searchTerm) {
      dispatch(updateTranscriptSearchPhrase(trimmedPhrase));
    }

    return phrase;
  };

  const incrementSearchIndex = () => {
    if (currentSearchIndex < searchTermIndices.length) {
      dispatch(updateCurrentSearchIndex(currentSearchIndex + 1));
    } else {
      dispatch(updateCurrentSearchIndex(1));
    }

    analytics.track(TRANSCRIPTION_SEARCH_NAVIGATED, {
      currentUserIsOwner,
      video_id: videoId,
    });
  };

  const decrementSearchIndex = () => {
    if (currentSearchIndex > 1) {
      dispatch(updateCurrentSearchIndex(currentSearchIndex - 1));
    } else {
      dispatch(updateCurrentSearchIndex(searchTermIndices.length));
    }

    analytics.track(TRANSCRIPTION_SEARCH_NAVIGATED, {
      currentUserIsOwner,
      video_id: videoId,
    });
  };

  const closeSearch = useCallback(
    ev => {
      if (isTranscriptSearchOpen && ev.keyCode === ESCAPE) {
        dispatch(toggleIsTranscriptSearchOpen(false));
      }
    },
    [isTranscriptSearchOpen, dispatch]
  );

  useEffect(() => {
    if (isTranscriptSearchOpen) {
      // @ts-expect-error ignore due to enabling strict null checks
      textInput.current.focus();
    }

    window.addEventListener('keyup', closeSearch);

    return () => {
      window.removeEventListener('keyup', closeSearch);
    };
  }, [isTranscriptSearchOpen, closeSearch]);

  useEffect(() => {
    if (Number(phrases?.length) > 0 && searchTerm.length > 0) {
      const indexArray = findIndicesOfSearchPhraseInPhrases(
        searchTerm,
        // @ts-expect-error ignore due to enabling strict null checks
        phrases
      );

      dispatch(updateTranscriptSearchPhraseIndexArray(indexArray));

      if (indexArray.length > 0) {
        dispatch(updateCurrentSearchIndex(1));
      } else {
        dispatch(updateCurrentSearchIndex(0));
      }
    }
  }, [searchTerm, phrases, dispatch]);

  return (
    <div
      className={classNames(
        styles.header,
        styles.headerSearch,
        styles.headerFirstLine
      )}
    >
      <div className="flex grow:1">
        <TextInput
          size="small"
          placeholder={SEARCH_TRANSCRIPT_TITLE}
          icon={<SvgSearch />}
          onChange={ev => {
            storeUserSearchedPhrase(ev.target.value);
          }}
          ref={textInput}
          onKeyDown={ev => {
            if (searchTermIndices.length > 0 && ev.which === 13) {
              incrementSearchIndex();
            }
          }}
          value={inputValue}
        />
      </div>

      <div className="flex items:center justify:flexEnd ">
        {searchTerm.length > 0 && (
          <>
            <Spacer left={1}>
              <div className={styles.monospaceText}>
                <Text>
                  {currentSearchIndex}/{searchTermIndices.length}
                </Text>
              </div>
            </Spacer>
            <Spacer left={1}>
              <div className="flex">
                <IconButton
                  size={iconSize}
                  altText="Previous Search Term"
                  icon={
                    iconSize === 'small' ? (
                      <SvgChevronSmallUp />
                    ) : (
                      <SvgChevronUp />
                    )
                  }
                  isDisabled={currentSearchIndex === 0}
                  onClick={decrementSearchIndex}
                />
                <IconButton
                  size={iconSize}
                  altText="Next Search Term"
                  icon={
                    iconSize === 'small' ? (
                      <SvgChevronSmallDown />
                    ) : (
                      <SvgChevronDown />
                    )
                  }
                  isDisabled={searchTermIndices.length === 0}
                  onClick={incrementSearchIndex}
                />
              </div>
            </Spacer>
          </>
        )}
        <Spacer left={0.5}>
          <TextButton
            onClick={() => {
              dispatch(toggleIsTranscriptSearchOpen(false));

              analytics.track(TRANSCRIPTION_SEARCH_CLOSED, {
                currentUserIsOwner,
                video_id: videoId,
              });
            }}
          >
            Cancel
          </TextButton>
        </Spacer>
      </div>
    </div>
  );
};

export default TranscriptHeaderSearch;
